/**
 * Horizon+ commerce components: add buttons, cross-sells, quantity breaks,
 * subscriptions and the automatic gift.
 *
 * Every cart change made here is announced with the same standard event the theme's own
 * product form dispatches, so the cart drawer, the cart page and the cart icon refresh
 * themselves without this file knowing about them.
 */

import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const SOURCE_ADD = 'hx-add';
const SOURCE_GIFT = 'hx-gift';

/**
 * @param {string} name
 * @param {CustomElementConstructor} constructor
 */
const define = (name, constructor) => {
  if (!customElements.get(name)) customElements.define(name, constructor);
};

/** @returns {Promise<any>} The current cart from the Ajax API. */
async function fetchCart() {
  const response = await fetch(`${Theme.routes.cart_url}.js`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
    cache: 'no-store',
  });
  if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
  return response.json();
}

/**
 * @param {string} url
 * @param {object} body
 */
async function postCart(url, body) {
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(body),
  });
  const data = await response.json();
  if (!response.ok || data.status) throw new Error(data.description || data.message || 'Cart request failed');
  return data;
}

/**
 * Runs a cart mutation and announces it so the rest of the theme re-renders.
 * @param {object} config
 * @param {'add' | 'remove'} config.action
 * @param {Array<object>} config.lines - Lines in the shape the standard event expects.
 * @param {string} config.source - Identifies the dispatcher in the resolved detail.
 * @param {() => Promise<unknown>} config.mutate
 * @param {EventTarget} [config.target]
 */
async function mutateCart({ action, lines, source, mutate, target = document }) {
  const deferred = CartLinesUpdateEvent.createPromise();
  // Listeners attach their own handlers; this one keeps a failed mutation from surfacing as unhandled.
  deferred.promise.catch(() => {});

  target.dispatchEvent(
    new CartLinesUpdateEvent({
      action,
      context: action === 'add' ? 'product' : 'cart',
      lines,
      promise: deferred.promise,
    })
  );

  try {
    await mutate();
    const cart = await fetchCart();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, itemCount: cart.item_count, source, didError: false },
    });
    return cart;
  } catch (error) {
    deferred.reject(error);
    throw error;
  }
}

/**
 * @param {Array<{ id: string | number, quantity: number, properties?: Record<string, string> }>} items
 * @param {string} [source]
 */
function addItems(items, source = SOURCE_ADD) {
  return mutateCart({
    action: 'add',
    source,
    lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
    mutate: () => postCart(Theme.routes.cart_add_url, { items }),
  });
}

/* -------------------------------------------------------------------------- */
/* Add buttons (`data-hx-add`)                                                */
/* -------------------------------------------------------------------------- */

document.addEventListener('click', async (event) => {
  if (!(event.target instanceof Element)) return;
  const button = event.target.closest('[data-hx-add]');
  if (!(button instanceof HTMLButtonElement) || button.disabled) return;

  // A variant select in the same item overrides the id rendered on the button.
  const select = button.closest('[data-hx-item]')?.querySelector('select[data-hx-variant]');
  const variantId = (select instanceof HTMLSelectElement ? select.value : '') || button.dataset.hxAdd;
  if (!variantId) return;

  button.disabled = true;
  button.setAttribute('aria-busy', 'true');

  try {
    await addItems([{ id: variantId, quantity: 1 }]);
  } catch (error) {
    console.warn('[hx-add] Add to cart failed:', error);
    window.hxToast?.(error instanceof Error ? error.message : '');
  } finally {
    button.disabled = false;
    button.removeAttribute('aria-busy');
  }
});

/* -------------------------------------------------------------------------- */
/* Shared base for blocks that follow the product form of their section       */
/* -------------------------------------------------------------------------- */

/** @type {Set<ProductFormCompanion>} */
const companions = new Set();

// The product form builds its payload from the <form> element, so companions write their
// hidden inputs just before the theme's own submit handler reads it.
document.addEventListener(
  'submit',
  (event) => {
    for (const companion of companions) {
      if (companion.form === event.target) companion.apply();
    }
  },
  true
);

class ProductFormCompanion extends HTMLElement {
  connectedCallback() {
    companions.add(this);
    this.root?.addEventListener(StandardEvents.productSelect, this.onProductSelect);
    this.setup();
    this.apply();
  }

  disconnectedCallback() {
    companions.delete(this);
    this.root?.removeEventListener(StandardEvents.productSelect, this.onProductSelect);
  }

  /** @returns {Element | null} The section or dialog that holds this block and its product form. */
  get root() {
    return this.closest('dialog, .shopify-section');
  }

  /** @returns {HTMLFormElement | null} */
  get form() {
    return this.root?.querySelector('product-form-component form[data-type="add-to-cart-form"]') ?? null;
  }

  /**
   * Creates, updates or removes a hidden input of the product form.
   * @param {string} name
   * @param {string | null} value - `null` removes the input.
   */
  setFormValue(name, value) {
    const form = this.form;
    if (!form) return;

    let input = form.querySelector(`input[data-hx-field="${name}"]`);

    if (value === null) {
      input?.remove();
      return;
    }

    if (!(input instanceof HTMLInputElement)) {
      input = document.createElement('input');
      input.setAttribute('type', 'hidden');
      input.setAttribute('name', name);
      input.setAttribute('data-hx-field', name);
      form.appendChild(input);
    }

    /** @type {HTMLInputElement} */ (input).value = value;
  }

  /**
   * After a variant change the block shows the prices of the new variant. The theme
   * sometimes morphs the whole page and sometimes only parts of it, so take the markup
   * from the response when this block was left untouched, then put the shopper's choice back.
   * @param {Event & { promise?: Promise<any> }} event
   */
  onProductSelect = (event) => {
    event.promise
      ?.then((/** @type {any} */ result) => {
        const html = result?.detail?.html;
        const fresh = this.id && html?.querySelector ? html.querySelector(`#${CSS.escape(this.id)}`) : null;

        if (fresh instanceof HTMLElement && fresh.dataset.variantId !== this.dataset.variantId) {
          this.innerHTML = fresh.innerHTML;
          this.dataset.variantId = fresh.dataset.variantId ?? '';
        }

        requestAnimationFrame(() => {
          this.restore();
          this.apply();
        });
      })
      .catch(() => {});
  };

  /** Binds listeners once, on connection. */
  setup() {}

  /** Writes the current choice to the product form. */
  apply() {}

  /** Puts the shopper's choice back on freshly rendered markup. */
  restore() {}
}

/* -------------------------------------------------------------------------- */
/* Quantity breaks                                                            */
/* -------------------------------------------------------------------------- */

class HxQuantityBreaks extends ProductFormCompanion {
  /** @type {string | null} */
  selected = null;

  setup() {
    const checked = this.querySelector('input[type="radio"]:checked');
    this.selected = checked instanceof HTMLInputElement ? checked.value : null;

    this.addEventListener('change', (event) => {
      if (!(event.target instanceof HTMLInputElement) || event.target.type !== 'radio') return;
      this.selected = event.target.value;
      this.apply();
    });

    // Keep the highlighted tier in line with the theme's own quantity selector.
    this.root?.addEventListener('quantity-selector:update', (event) => {
      const quantity = /** @type {any} */ (event).detail?.quantity;
      const cartLine = /** @type {any} */ (event).detail?.cartLine;
      if (cartLine || quantity === undefined) return;
      this.selected = String(quantity);
      this.restore();
    });
  }

  restore() {
    for (const radio of this.querySelectorAll('input[type="radio"]')) {
      if (radio instanceof HTMLInputElement) radio.checked = radio.value === this.selected;
    }
  }

  apply() {
    const form = this.form;
    if (!form || this.selected === null) return;
    if (!this.querySelector(`input[type="radio"][value="${CSS.escape(this.selected)}"]`)) return;

    const selector = /** @type {any} */ (form.querySelector('quantity-selector-component'));
    const quantityInput = form.querySelector('input[name="quantity"]:not([data-hx-field])');

    if (typeof selector?.setValue === 'function') {
      selector.setValue(this.selected);
      this.setFormValue('quantity', null);
    } else if (quantityInput instanceof HTMLInputElement) {
      quantityInput.value = this.selected;
      this.setFormValue('quantity', null);
    } else {
      this.setFormValue('quantity', this.selected);
    }
  }
}

define('hx-quantity-breaks', HxQuantityBreaks);

/* -------------------------------------------------------------------------- */
/* Subscription                                                               */
/* -------------------------------------------------------------------------- */

class HxSubscription extends ProductFormCompanion {
  /** @type {'onetime' | 'subscription'} */
  mode = 'onetime';

  /** @type {string | null} */
  planId = null;

  setup() {
    this.read();
    this.addEventListener('change', () => {
      this.read();
      this.apply();
    });
  }

  read() {
    const checked = this.querySelector('input[data-hx-purchase]:checked');
    this.mode = checked instanceof HTMLInputElement && checked.value === 'subscription' ? 'subscription' : 'onetime';

    const select = this.querySelector('select[data-hx-plan]');
    if (select instanceof HTMLSelectElement) this.planId = select.value;
  }

  restore() {
    for (const radio of this.querySelectorAll('input[data-hx-purchase]')) {
      if (radio instanceof HTMLInputElement) radio.checked = radio.value === this.mode;
    }

    const select = this.querySelector('select[data-hx-plan]');
    if (select instanceof HTMLSelectElement && this.planId) {
      const hasPlan = Array.from(select.options).some((option) => option.value === this.planId);
      if (hasPlan) select.value = this.planId;
      else this.planId = select.value;
    }
  }

  apply() {
    const select = this.querySelector('select[data-hx-plan]');
    const planId = select instanceof HTMLSelectElement ? select.value : '';
    const subscribed = this.mode === 'subscription' && planId !== '';

    this.setFormValue('selling_plan', subscribed ? planId : null);
    this.toggleAttribute('data-subscribed', subscribed);

    // Show the price of the selected plan.
    for (const price of this.querySelectorAll('[data-hx-plan-price]')) {
      if (price instanceof HTMLElement) price.hidden = price.dataset.hxPlanPrice !== planId;
    }
  }
}

define('hx-subscription', HxSubscription);

/* -------------------------------------------------------------------------- */
/* Cross-sell                                                                 */
/* -------------------------------------------------------------------------- */

class HxCrossSell extends HTMLElement {
  /** @type {boolean | null} */
  checked = null;

  connectedCallback() {
    this.addEventListener('change', this.onChange);
    this.root?.addEventListener(StandardEvents.cartLinesUpdate, this.onCartLinesUpdate);
    this.root?.addEventListener(StandardEvents.productSelect, this.onProductSelect);
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.onChange);
    this.root?.removeEventListener(StandardEvents.cartLinesUpdate, this.onCartLinesUpdate);
    this.root?.removeEventListener(StandardEvents.productSelect, this.onProductSelect);
  }

  get root() {
    return this.closest('dialog, .shopify-section');
  }

  /** @returns {HTMLInputElement | null} */
  get toggle() {
    const toggle = this.querySelector('input[data-hx-toggle]');
    return toggle instanceof HTMLInputElement ? toggle : null;
  }

  get variantId() {
    const select = this.querySelector('select[data-hx-variant]');
    return (select instanceof HTMLSelectElement ? select.value : '') || this.dataset.variantId || '';
  }

  /** @param {Event} event */
  onChange = (event) => {
    if (event.target === this.toggle) this.checked = this.toggle?.checked ?? null;
  };

  /** A variant change can morph the checkbox back to its server state. */
  onProductSelect = (/** @type {any} */ event) => {
    event.promise
      ?.then(() =>
        requestAnimationFrame(() => {
          if (this.checked !== null && this.toggle) this.toggle.checked = this.checked;
        })
      )
      .catch(() => {});
  };

  /**
   * Adds the ticked add-on once the main product has been added by the product form.
   * @param {CartLinesUpdateEvent} event
   */
  onCartLinesUpdate = (event) => {
    if (this.dataset.mode !== 'toggle' || event.action !== 'add') return;
    if (!(event.target instanceof Element) || !event.target.closest('product-form-component')) return;
    if (!this.toggle?.checked || !this.variantId) return;

    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;
        return addItems([{ id: this.variantId, quantity: 1 }]);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[hx-cross-sell] Add-on was not added:', error);
      });
  };
}

define('hx-cross-sell', HxCrossSell);

/* -------------------------------------------------------------------------- */
/* Automatic gift                                                             */
/* -------------------------------------------------------------------------- */

const GIFT_PROPERTY = '_hx_gift';
const GIFT_PRESENT_KEY = 'hx:gift:present';
const GIFT_DECLINED_KEY = 'hx:gift:declined';

/**
 * @param {string} key
 * @returns {boolean}
 */
function readFlag(key) {
  try {
    return sessionStorage.getItem(key) === '1';
  } catch (_) {
    return false;
  }
}

/**
 * @param {string} key
 * @param {boolean} value
 */
function writeFlag(key, value) {
  try {
    if (value) sessionStorage.setItem(key, '1');
    else sessionStorage.removeItem(key);
  } catch (_) {
    // Without storage the gift is simply offered again.
  }
}

class HxAutoGift extends HTMLElement {
  /** @type {boolean} */
  running = false;

  /** @type {boolean} */
  queued = false;

  connectedCallback() {
    document.addEventListener(StandardEvents.cartLinesUpdate, this.onCartLinesUpdate);
    this.reconcile();
  }

  disconnectedCallback() {
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.onCartLinesUpdate);
  }

  /** @param {CartLinesUpdateEvent} event */
  onCartLinesUpdate = (event) => {
    event.promise
      ?.then(({ detail }) => {
        // The gift's own mutations must not trigger another pass.
        if (detail?.source !== SOURCE_GIFT) this.reconcile();
      })
      .catch(() => {});
  };

  async reconcile() {
    if (window.Shopify?.designMode) return;

    if (this.running) {
      this.queued = true;
      return;
    }

    this.running = true;

    try {
      const variantId = Number(this.dataset.variantId);
      const threshold = Number(this.dataset.threshold);
      if (!variantId || !(threshold >= 0)) return;

      const cart = await fetchCart();
      /** @type {any[]} */
      const items = cart.items ?? [];
      const giftLine = items.find((item) => item.variant_id === variantId && item.properties?.[GIFT_PROPERTY]);
      const otherLines = items.filter((item) => item !== giftLine);
      const total = cart.total_price - (giftLine ? giftLine.final_line_price : 0);
      const eligible = otherLines.length > 0 && total >= threshold;

      if (!eligible) {
        writeFlag(GIFT_DECLINED_KEY, false);
        writeFlag(GIFT_PRESENT_KEY, false);

        if (giftLine) {
          await mutateCart({
            action: 'remove',
            source: SOURCE_GIFT,
            lines: [{ id: giftLine.key, quantity: 0 }],
            mutate: () => postCart(Theme.routes.cart_change_url, { id: giftLine.key, quantity: 0 }),
          });
        }
        return;
      }

      if (giftLine) {
        writeFlag(GIFT_PRESENT_KEY, true);
        return;
      }

      // The gift was in the cart and is gone while still earned: the shopper removed it.
      if (readFlag(GIFT_PRESENT_KEY)) {
        writeFlag(GIFT_PRESENT_KEY, false);
        writeFlag(GIFT_DECLINED_KEY, true);
      }

      if (readFlag(GIFT_DECLINED_KEY) || this.dataset.available !== 'true') return;

      await addItems([{ id: variantId, quantity: 1, properties: { [GIFT_PROPERTY]: '1' } }], SOURCE_GIFT);
      writeFlag(GIFT_PRESENT_KEY, true);
    } catch (error) {
      console.warn('[hx-auto-gift] Could not update the gift:', error);
    } finally {
      this.running = false;

      if (this.queued) {
        this.queued = false;
        this.reconcile();
      }
    }
  }
}

define('hx-auto-gift', HxAutoGift);
